import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  Chip,
  Box,
} from '@mui/material';
import { Edit2, Trash2 } from 'lucide-react';
import { Resource } from '../../types/resource';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';

interface ResourceTableProps {
  resources: Resource[];
  onEdit: (resource: Resource) => void;
  onDelete: (id: number) => void;
  onSelect: (resource: Resource) => void;
}

export default function ResourceTable({
  resources,
  onEdit,
  onDelete,
  onSelect,
}: ResourceTableProps) {
  const theme = useTheme();
  const { role } = useAuth();

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>Name</TableCell>
            <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Contact</TableCell>
            <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Services</TableCell>
            <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Timezone</TableCell>
            {role !== AUTH_CONSTANTS.ROLES.USER ? (
              <TableCell align="right" sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
                Actions
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {resources.map(resource => (
            <TableRow
              key={resource.id}
              hover
              onClick={() => onSelect(resource)}
              sx={{
                cursor: 'pointer',
                '&:last-child td, &:last-child th': { border: 0 },
              }}
            >
              <TableCell sx={{ px: 3, fontWeight: 400, fontSize: 16 }}>{resource.name}</TableCell>
              <TableCell>
                <Box>
                  {resource.email}
                  <br />
                  {resource.phone}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {resource.serviceResourceMapping?.map(({ services }) => (
                    <Chip
                      key={services.id}
                      label={services.name}
                      size="small"
                      sx={{
                        backgroundColor: `${theme.palette.primary.main}10`,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ))}
                </Box>
              </TableCell>
              <TableCell>{resource.timezone}</TableCell>
              {role !== AUTH_CONSTANTS.ROLES.USER ? (
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      sx={{ mr: 2 }}
                      onClick={e => {
                        e.stopPropagation();
                        onEdit(resource);
                      }}
                    >
                      <Edit2 size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(resource.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
